// Utility functions for handling API errors and server connectivity issues

/**
 * Check if the error is specifically a KOACH server issue (server down/not responding)
 * @param {Error} error - The error object from axios
 * @returns {boolean} - True if it's a KOACH server issue
 */
export const isKoachServerError = (error) => {
    // Server errors (5xx status codes) - server is down or having issues
    if (error.response && error.response.status >= 500) {
        return true;
    }

    // Connection refused specifically to the server (server not running)
    if (error.code === 'ECONNREFUSED') {
        return true;
    }

    // DNS resolution failed for the specific server domain
    if (error.code === 'ENOTFOUND' && error.config && error.config.url &&
        error.config.url.includes('koach.kauveryhospital.com')) {
        return true;
    }

    return false;
};

/**
 * Check if the error is an internet connectivity issue
 * @param {Error} error - The error object from axios
 * @returns {boolean} - True if it's an internet connectivity issue
 */
export const isInternetConnectivityError = (error) => {
    // No response received and not a server-specific error
    if (!error.response && !isKoachServerError(error)) {
        // General network error (likely internet connectivity)
        if (error.code === 'ERR_NETWORK' || error.code === 'NETWORK_ERROR') {
            return true;
        }

        // DNS resolution failed for any domain (likely internet issue)
        if (error.code === 'ENOTFOUND') {
            return true;
        }
    }

    return false;
};

/**
 * Check if the error is a timeout issue
 * @param {Error} error - The error object from axios
 * @returns {boolean} - True if it's a timeout issue
 */
export const isTimeoutError = (error) => {
    return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
};

/**
 * Get appropriate error message based on error type
 * @param {Error} error - The error object from axios
 * @returns {string} - The error message to display
 */
export const getErrorMessage = (error) => {
    if (isKoachServerError(error)) {
        return "Unable to connect KOACH server, try after sometime. For further queries please contact the KOACH administrator at 9940287705";
    } else if (isInternetConnectivityError(error)) {
        return "Internet not available. Please check your internet connection and try again.";
    } else if (isTimeoutError(error)) {
        return "Request timed out. Please check your connection and try again.";
    } else if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
    } else if (error.response && error.response.status === 404) {
        return "The requested resource was not found.";
    } else if (error.response && error.response.status === 401) {
        return "Authentication failed. Please login again.";
    } else if (error.response && error.response.status === 403) {
        return "Access denied. You don't have permission to perform this action.";
    } else {
        return "An unexpected error occurred. Please try again.";
    }
};

/**
 * Handle API errors with appropriate user feedback
 * @param {Error} error - The error object from axios
 * @param {Function} showModal - Function to show error modal (optional)
 * @returns {string} - The error message
 */
export const handleApiError = (error, showModal = null) => {
    console.error('API Error:', error);

    const errorMessage = getErrorMessage(error);

    // Show modal if function is provided, otherwise use alert
    if (showModal && typeof showModal === 'function') {
        showModal(errorMessage);
    } else {
        alert(errorMessage);
    }

    return errorMessage;
};
